import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  useCreateStripePaymentMutation,
  useFinalizeStripePaymentMutation,
} from "../../../../store/Company/companyApiSlice";
import {
  useGetBillingAddressByBillingIdQuery,
  useGetBillingAddressByUserIdQuery,
  useDeleteBillingAddressMutation,
} from "../../../../store/Billing/billingApiSlice";
import { selectActiveCompanyId } from "../../../../store/activeCompanySlice";
import { PLAN_YEARLY } from "../../../../constants/webProfilePricing";
import { CommonToast } from "../../../common/Toast";
import {
  createPaymentBody,
  finalizeOutcome,
  paymentMetaData,
  primaryAddressOf,
  shippingAddressesOf,
} from "./paymentDetailsFields";

/* The checkout itself, transcribed from PaymentsDetailsForm.jsx. */
export const usePaymentCheckout = ({ planDetails, isReportRequest } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.auth);
  const actingCompanyId = useSelector(selectActiveCompanyId);

  const mode = planDetails?.mode || "payg";
  const membershipPlan = planDetails?.plan || PLAN_YEARLY;
  const isBundle = mode === "bundle";

  const [paymentBreakdown, setPaymentBreakdown] = useState();
  const [clientSecret, setClientSecret] = useState(null);
  const [pendingSetup, setPendingSetup] = useState(false);
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [userID, setUserID] = useState(null);
  const [billingId, setBillingId] = useState("");
  const [hasAddressDataChanged, setHasAddressDataChanged] = useState(false);

  /* Which dialog is open, if any: "list" | "add" | "edit" | null. The live
     form keeps three booleans and closes the others by hand in each handler;
     one value cannot contradict itself. */
  const [dialog, setDialog] = useState(null);

  const [createStripePayment, { isLoading: isCreateLoading }] =
    useCreateStripePaymentMutation();
  const [finalizeStripePayment] = useFinalizeStripePaymentMutation("");
  const [deleteBillingAddresss] = useDeleteBillingAddressMutation();

  const { data: addresses, refetch } = useGetBillingAddressByUserIdQuery({
    id: userID,
    token,
  });
  const { data: existingBilling, refetch: refetchExistingBilling } =
    useGetBillingAddressByBillingIdQuery({ id: billingId, token });

  const shippingAddress = shippingAddressesOf(addresses);
  const primary = primaryAddressOf(addresses);
  const hasBillingAddress = Boolean(addresses && addresses.length > 0);

  useEffect(() => {
    setUserID(userInfo?.individual?.[0]?.individualID);
  }, [userInfo]);

  /* TIP-493 — stash the flow so the post-Stripe-redirect success page can pick
     its copy; localStorage survives the redirect, React state does not. */
  useEffect(() => {
    localStorage.setItem("webProfilePurchaseMode", mode);
  }, [mode]);

  useEffect(() => {
    refetch();
  }, [refetch, hasAddressDataChanged]);

  const createPaymentEvent = useCallback(async () => {
    const metaData = paymentMetaData({
      billingAddressId: shippingAddress?.[0]?.billing_add_ID,
      userId: userInfo?.individual?.[0]?.userID,
      isReportRequest,
    });
    try {
      const response = await createStripePayment({
        data: createPaymentBody({
          mode,
          membershipPlan,
          actingCompanyId,
          purchaseTrackId: location.state?.purchase_track_id,
          metaData,
        }),
        token,
      }).unwrap();
      setPaymentBreakdown(response);
    } catch (error) {
      CommonToast.error(error?.data?.error || "Error preparing payment");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, membershipPlan, actingCompanyId, token, addresses, isReportRequest]);

  /* 1) Create the payment once a billing address is available. */
  useEffect(() => {
    if (hasBillingAddress && !paymentBreakdown) createPaymentEvent();
  }, [hasBillingAddress, paymentBreakdown, createPaymentEvent]);

  /* 2) Auto-finalize to reveal the Stripe card form (one combined invoice). */
  useEffect(() => {
    const finalize = async () => {
      try {
        const response = await finalizeStripePayment({
          token,
          data: { invoice_id: paymentBreakdown?.invoice_id, recurring: isBundle },
        });
        const secret = response?.data?.client_secret;
        const outcome = finalizeOutcome(secret);

        if (outcome === "paid") {
          setPaidDialogOpen(true);
        } else if (outcome === "ready") {
          setClientSecret(secret.toString());
          setPendingSetup(response?.data?.pending_setup);
          setShowStripePayment(true);
        } else {
          CommonToast.error("Unable to start payment");
        }
      } catch (e) {
        CommonToast.error("Unable to start payment");
      }
    };
    if (paymentBreakdown?.invoice_id && !clientSecret && !showStripePayment) {
      finalize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentBreakdown?.invoice_id]);

  const closeDialog = () => setDialog(null);
  const openAddressList = () => setDialog("list");
  const openAddAddress = () => setDialog("add");
  const openEditAddress = (id) => {
    setBillingId(id);
    setHasAddressDataChanged((v) => !v);
    setDialog("edit");
  };

  /* Transcribed from EditBillingAddress's own success path: a saved edit reopens the address list and closes the edit form, in that order. */
  const closeEditDialog = () => setDialog((d) => (d === "edit" ? null : d));

  const deleteAddress = async (id) => {
    try {
      await deleteBillingAddresss({ data: id, token });
      setHasAddressDataChanged((v) => !v);
    } catch (e) {
      CommonToast.error("Error deleting billing address.");
    }
  };

  const finishPaidDialog = () => {
    setPaidDialogOpen(false);
    navigate("/plans/paymentsuccessful");
  };

  return {
    userInfo,
    /* create_payment's answer, which carries `extra_details` — Stripe's own
       country-aware tax figures for this invoice. */
    paymentBreakdown,
    addresses,
    primary,
    shippingAddress,
    hasBillingAddress,
    existingBilling,
    refetchExistingBilling,
    hasAddressDataChanged,
    setHasAddressDataChanged,

    dialog,
    closeDialog,
    closeEditDialog,
    openAddressList,
    openAddAddress,
    openEditAddress,
    deleteAddress,

    clientSecret,
    pendingSetup,
    paidDialogOpen,
    finishPaidDialog,
    /* Transcribed spinner condition: while the invoice is being created, and
       while there is still no secret and no paid invoice. */
    preparing: isCreateLoading || (!clientSecret && !paidDialogOpen),
  };
};

export default usePaymentCheckout;
